import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, RootDispatch } from "../../store";
import {
  downloadWarrantyPdf,
  fetchWarrantyDetails,
} from "../../store/slices/warrantyDetailsSlice";
import WarrantyDetailsModal from "./WarrantyDetailsModal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import debounce from "lodash/debounce";
import { toast } from "react-toastify";
import { WarrantyDetails } from "../../models/warrantlyDetails.model";
import ResendModal from "./ResendModal";
import { getAllEpcNamesIds } from "../../store/slices/epcSlice";
import Select from "react-select";
import { Edit2 } from "lucide-react";

const WarrantyDetailsPage: React.FC = () => {
  const dispatch: RootDispatch = useDispatch();
  const {
    warrantyDetails,
    totalItems,
    totalPages,
    page,
    limit,
    loading,
    error,
    downloadError,
  } = useSelector((state: RootState) => state.warrantyDetails);
  const { epc: epcs } = useSelector((state: RootState) => state.epc);
  const [selectedEpcId, setSelectedEpcId] = useState<string | "">("");
  const [inputValue, setInputValue] = useState("");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateFilterType, setDateFilterType] = useState<
    "purchaseDate" | "warrantyEndDate" | ""
  >("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedWarrantyId, setSelectedWarrantyId] = useState<string | null>(
    null,
  );
  const [resendWarrantyId, setResendWarrantyId] = useState<string | null>(null);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearch(value);
      setCurrentPage(1);
    }, 500),
    [],
  );

  useEffect(() => {
    dispatch(
      fetchWarrantyDetails({
        page: currentPage,
        limit,
        search,
        ...(startDate &&
          endDate &&
          dateFilterType && {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            dateFilterType,
          }),
        ...(selectedEpcId ? { epcId: selectedEpcId } : {}),
      }),
    );
  }, [
    dispatch,
    currentPage,
    limit,
    search,
    startDate,
    endDate,
    dateFilterType,
    selectedEpcId,
  ]);

  useEffect(() => {
    if (downloadError) {
      toast.error(downloadError);
    }
  }, [downloadError]);

  useEffect(() => {
    dispatch(getAllEpcNamesIds());
  }, [dispatch]);

  const handleDateFilter = () => {
    if (startDate && endDate && dateFilterType) {
      setCurrentPage(1);
      const adjustedEndDate = new Date(endDate);
      adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);

      dispatch(
        fetchWarrantyDetails({
          page: 1,
          limit,
          search,
          startDate: startDate.toISOString(),
          endDate: adjustedEndDate.toISOString(),
          dateFilterType,
        }),
      );
    } else {
      toast.error("Please select both dates and a date filter type");
    }
  };

  const handleReset = () => {
    setInputValue("");
    setSearch("");
    setStartDate(null);
    setEndDate(null);
    setDateFilterType("");
    setCurrentPage(1);
    setSelectedEpcId("");

    dispatch(
      fetchWarrantyDetails({
        page: 1,
        limit,
        search: "",
      }),
    );
  };

  const handleDownload = async (id: string, name: string) => {
    try {
      const result = await dispatch(downloadWarrantyPdf({ id, name })).unwrap();
      if (!result.success) {
        toast.error("Failed to download warranty PDF");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to download warranty PDF");
    }
  };

  const epcOptions = epcs.map((epc) => ({
    value: epc._id,
    label: epc.name || "NA",
  }));

  const formatDate = (dateString: string | undefined) => {
    if (dateString && !isNaN(new Date(dateString).getTime())) {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    }
    return "NA";
  };

  return (
    <div className="min-h-screen bg-slate-50/50 font-sans antialiased text-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Loading and Error Feedback overlays optimized */}
        {loading && (
          <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 mb-4 rounded-xl shadow-sm flex items-center justify-between animate-pulse">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
              <span className="font-medium text-sm">
                Updating warranty listings...
              </span>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 mb-4 rounded-xl shadow-sm flex items-center gap-2.5 text-sm">
            <svg
              className="h-5 w-5 text-rose-500 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <p className="font-medium">System Error: {error}</p>
          </div>
        )}

        {downloadError && (
          <div className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 mb-4 rounded-xl shadow-sm flex items-center gap-2.5 text-sm">
            <svg
              className="h-5 w-5 text-rose-500 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <p className="font-medium">Download Error: {downloadError}</p>
          </div>
        )}

        {/* Dashboard Frame Container */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
          {/* Dashboard Header Panel */}
          <div className="px-6 py-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white">
            <div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2.5">
                <svg
                  className="w-5 h-5 text-[#0077C0]"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Warranty Records
              </h1>
              <p className="text-xs text-slate-500 mt-0.5 font-medium">
                Manage and review issued tracking profiles
              </p>
            </div>
            <div className="inline-flex items-center px-3 py-1 bg-slate-100 text-slate-700 font-semibold text-xs rounded-full self-start sm:self-auto">
              Total items: {totalItems || 0}
            </div>
          </div>

          {/* Filtering Control Section */}
          <div className="p-6 bg-slate-50/60 border-b border-slate-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4">
              <div className="sm:col-span-2 lg:col-span-4">
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                  Search Profiles
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by Name, Email, Mobile..."
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      debouncedSearch(e.target.value);
                    }}
                    className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#0077C0] transition-all placeholder:text-slate-400"
                  />
                  <svg
                    className="absolute left-3 top-2.5 w-4 h-4 text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>

              <div className="lg:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                  EPC Channel
                </label>
                <Select
                  options={epcOptions}
                  value={
                    epcOptions.find((opt) => opt.value === selectedEpcId) ||
                    null
                  }
                  onChange={(option: any) => {
                    setSelectedEpcId(option ? option.value : "");
                    setCurrentPage(1);
                  }}
                  placeholder="All Channels"
                  isClearable
                  isSearchable
                  className="text-sm"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: "0.75rem",
                      borderColor: "#e2e8f0",
                      paddingTop: "2px",
                      paddingBottom: "2px",
                      boxShadow: "none",
                      "&:hover": { borderColor: "#cbd5e1" },
                    }),
                  }}
                />
              </div>

              <div className="lg:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                  Timeline Reference
                </label>
                <select
                  value={dateFilterType}
                  onChange={(e) =>
                    setDateFilterType(
                      e.target.value as "purchaseDate" | "warrantyEndDate" | "",
                    )
                  }
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#0077C0] transition-all"
                >
                  <option value="">Select Target Date</option>
                  <option value="purchaseDate">Purchase Date</option>
                  <option value="warrantyEndDate">Warranty Expiry</option>
                </select>
              </div>

              <div className="lg:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                  From Date
                </label>
                <DatePicker
                  selected={startDate}
                  onChange={(date: Date | null) => setStartDate(date)}
                  dateFormat="dd/MM/yyyy"
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#0077C0] transition-all"
                  placeholderText="DD/MM/YYYY"
                />
              </div>

              <div className="lg:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                  To Date
                </label>
                <DatePicker
                  selected={endDate}
                  onChange={(date: Date | null) => setEndDate(date)}
                  dateFormat="dd/MM/yyyy"
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#0077C0] transition-all"
                  placeholderText="DD/MM/YYYY"
                />
              </div>

              <div className="sm:col-span-2 lg:col-span-12 flex justify-end gap-3 mt-2">
                <button
                  onClick={handleReset}
                  className="px-4 py-2 bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl hover:bg-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all uppercase tracking-wide"
                >
                  Clear Filters
                </button>
                <button
                  onClick={handleDateFilter}
                  className="px-5 py-2 bg-[#0077C0] text-white text-xs font-semibold rounded-xl hover:bg-[#005B90] focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all uppercase tracking-wide shadow-sm"
                >
                  Apply Filter
                </button>
              </div>
            </div>
          </div>

          {/* Desktop Table View Block (Hidden on Small Viewports) */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-3.5">Client Profile</th>
                  <th className="px-4 py-3.5">Contact Details</th>
                  <th className="px-4 py-3.5">Serial No. (S/N)</th>
                  <th className="px-4 py-3.5">Product</th>
                  <th className="px-4 py-3.5">EPC</th>
                  <th className="px-4 py-3.5">Purchase Date</th>
                  <th className="px-4 py-3.5">Warranty End</th>
                  <th className="px-4 py-3.5">Destination Address</th>
                  <th className="px-4 py-3.5 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {warrantyDetails.length
                  ? warrantyDetails.map(
                      (item: WarrantyDetails, index: number) => (
                        <tr
                          key={item._id}
                          onClick={(e) => {
                            if (!(e.target as HTMLElement).closest("button")) {
                              setSelectedWarrantyId(item._id);
                            }
                          }}
                          className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                        >
                          <td className="px-4 py-4">
                            <div className="font-semibold text-slate-900 group-hover:text-[#0077C0] transition-colors">
                              {item.name || "NA"}
                            </div>
                          </td>
                          <td className="px-4 py-4 space-y-0.5">
                            <div className="text-slate-600 text-xs font-medium">
                              {item.email || "NA"}
                            </div>
                            <div className="text-slate-400 text-xs">
                              {item.mobileNumber || "NA"}
                            </div>
                          </td>
                          <td className="px-4 py-4 font-mono text-xs text-slate-600">
                            {item.sn || "NA"}
                          </td>
                          <td className="px-4 py-4 font-medium text-slate-700">
                            {item.product || "NA"}
                          </td>
                          <td className="px-4 py-4">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-800">
                              {item.epcName || "NA"}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-slate-600 text-xs">
                            {formatDate(item.purchaseDate)}
                          </td>
                          <td className="px-4 py-4 text-slate-600 text-xs font-medium">
                            {formatDate(item.warrantyEndDate)}
                          </td>
                          <td className="px-4 py-4 max-w-[200px] truncate text-xs text-slate-500">
                            {[
                              item.address,
                              item.city,
                              item.district,
                              item.state,
                              item.pincode,
                            ]
                              .filter(Boolean)
                              .join(", ") || "NA"}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => setResendWarrantyId(item._id)}
                                disabled={loading}
                                title="Resend via WhatsApp"
                                className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.074-.149-.669-.719-.911-.99-.241-.271-.482-.448-.669-.448h-.569c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.04 2c-5.514 0-9.98 4.466-9.98 9.98 0 1.76.46 3.47 1.333 4.994L2 22l5.145-1.345a9.93 9.93 0 004.895 1.261h.001c5.513 0 9.979-4.466 9.979-9.98 0-2.667-1.04-5.174-2.929-7.063A9.935 9.935 0 0012.04 2z" />
                                </svg>
                              </button>
                              <button
                                onClick={() =>
                                  handleDownload(item._id, item.name)
                                }
                                title="Download PDF"
                                className="p-2 text-[#0077C0] hover:bg-sky-50 rounded-lg transition-colors"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 4v12m0 0l-4-4m4 4l4-4m-8 4v4h8v-4"
                                  />
                                </svg>
                              </button>

                              <button
                                onClick={() => setSelectedWarrantyId(item._id)}
                                className="p-1 rounded-md hover:bg-slate-200 focus:outline-none transition-colors"
                                title="Edit adjustment"
                              >
                                <Edit2 className="w-4 h-4 text-slate-500 hover:text-blue-600" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ),
                    )
                  : null}
              </tbody>
            </table>
          </div>

          {/* Mobile Structured Card Layout View (Active on Small Screen Devices) */}
          <div className="block md:hidden bg-slate-50/40 p-4 space-y-4">
            {warrantyDetails.length
              ? warrantyDetails.map((item: WarrantyDetails) => (
                  <div
                    key={item._id}
                    onClick={(e) => {
                      if (!(e.target as HTMLElement).closest("button")) {
                        setSelectedWarrantyId(item._id);
                      }
                    }}
                    className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3.5 relative active:scale-[0.99] transition-transform"
                  >
                    <div className="flex justify-between items-start border-b border-slate-100 pb-2.5">
                      <div>
                        <h3 className="font-bold text-slate-900 text-sm">
                          {item.name || "NA"}
                        </h3>
                        <p className="font-mono text-[11px] text-slate-500 mt-0.5">
                          S/N: {item.sn || "NA"}
                        </p>
                      </div>
                      <span className="px-2 py-0.5 text-[10px] font-semibold bg-blue-50 text-[#0077C0] rounded-md">
                        {item.epcName || "NA"}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-x-2 gap-y-3 text-xs">
                      <div>
                        <span className="block text-slate-400 font-medium text-[10px] uppercase tracking-wider">
                          Product
                        </span>
                        <span className="font-medium text-slate-800">
                          {item.product || "NA"}
                        </span>
                      </div>
                      <div>
                        <span className="block text-slate-400 font-medium text-[10px] uppercase tracking-wider">
                          Contact
                        </span>
                        <span className="text-slate-700 block truncate">
                          {item.mobileNumber || "NA"}
                        </span>
                      </div>
                      <div>
                        <span className="block text-slate-400 font-medium text-[10px] uppercase tracking-wider">
                          Purchase Date
                        </span>
                        <span className="text-slate-600">
                          {formatDate(item.purchaseDate)}
                        </span>
                      </div>
                      <div>
                        <span className="block text-slate-400 font-medium text-[10px] uppercase tracking-wider">
                          Warranty Expiry
                        </span>
                        <span className="text-rose-600 font-medium">
                          {formatDate(item.warrantyEndDate)}
                        </span>
                      </div>
                      <div className="col-span-2">
                        <span className="block text-slate-400 font-medium text-[10px] uppercase tracking-wider">
                          Address
                        </span>
                        <p className="text-slate-500 line-clamp-1">
                          {[
                            item.address,
                            item.city,
                            item.district,
                            item.state,
                            item.pincode,
                          ]
                            .filter(Boolean)
                            .join(", ") || "NA"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-3 mt-1">
                      <button
                        onClick={() => setResendWarrantyId(item._id)}
                        disabled={loading}
                        className="px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-lg flex items-center gap-1.5 active:bg-emerald-100 transition-colors"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.074-.149-.669-.719-.911-.99-.241-.271-.482-.448-.669-.448h-.569c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.04 2c-5.514 0-9.98 4.466-9.98 9.98 0 1.76.46 3.47 1.333 4.994L2 22l5.145-1.345a9.93 9.93 0 004.895 1.261h.001c5.513 0 9.979-4.466 9.979-9.98 0-2.667-1.04-5.174-2.929-7.063A9.935 9.935 0 0012.04 2z" />
                        </svg>
                        Share
                      </button>
                      <button
                        onClick={() => handleDownload(item._id, item.name)}
                        className="px-3 py-1.5 bg-sky-50 text-[#0077C0] text-xs font-semibold rounded-lg flex items-center gap-1.5 active:bg-sky-100 transition-colors"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 4v12m0 0l-4-4m4 4l4-4m-8 4v4h8v-4"
                          />
                        </svg>
                        PDF
                      </button>
                      <button
                        onClick={() => setSelectedWarrantyId(item._id)}
                        className="px-3 py-1.5 bg-slate-300 text-slate-600 text-xs font-semibold rounded-lg flex items-center gap-1.5 active:bg-sky-100 transition-colors"
                        title="Edit adjustment"
                      >
                        <Edit2 className="w-4 h-4 text-slate-500 hover:text-blue-600" />
                        Edit
                      </button>
                    </div>
                  </div>
                ))
              : null}
          </div>

          {/* Fallback Empty Visual State Panel */}
          {!warrantyDetails.length && (
            <div className="py-16 text-center bg-white border-t border-slate-100">
              <svg
                className="w-12 h-12 mx-auto text-slate-300 mb-3"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h4 className="text-sm font-semibold text-slate-800">
                No records found
              </h4>
              <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">
                {search || dateFilterType
                  ? "Adjust your search parameters and filters and try searching again."
                  : "There are currently no warranty registries on file."}
              </p>
            </div>
          )}

          {/* Footer Controls / Pagination Navigation panel */}
          <div className="bg-white px-6 py-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-xs font-medium text-slate-500 order-2 sm:order-1">
              Showing Page{" "}
              <span className="text-slate-800 font-semibold">{page}</span> of{" "}
              <span className="text-slate-800 font-semibold">
                {totalPages || 1}
              </span>
            </div>
            <div className="flex items-center gap-2.5 w-full sm:w-auto order-1 sm:order-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={page === 1}
                className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-600 bg-white hover:bg-slate-50 disabled:bg-slate-50 disabled:text-slate-300 disabled:border-slate-100 transition-colors shadow-sm"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={page === totalPages || (totalItems ?? 0) === 0}
                className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2 bg-[#0077C0] rounded-xl text-xs font-semibold text-white hover:bg-[#005B90] disabled:bg-slate-100 disabled:text-slate-400 transition-colors shadow-sm"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Background Modals remain connected */}
      <WarrantyDetailsModal
        isOpen={!!selectedWarrantyId}
        onClose={() => setSelectedWarrantyId(null)}
        warrantyId={selectedWarrantyId || ""}
      />
      <ResendModal
        isOpen={!!resendWarrantyId}
        onClose={() => setResendWarrantyId(null)}
        warrantyId={resendWarrantyId || ""}
      />
    </div>
  );
};

export default WarrantyDetailsPage;
